import {
  type CreateBlacklistBody,
  activateBlacklistEntryService,
  createBlacklistEntryService,
  deactivateBlacklistEntryService,
  getBlacklistEntryByIdService,
  getBlacklistService,
} from "@/services/blacklist.service";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";

const BLACKLIST_KEY = ["blacklist"] as const;

interface BlacklistFilters {
  page?: number;
  limit?: number;
  entryType?: string;
  source?: string;
  isActive?: boolean;
  search?: string;
}

export const useBlacklist = (filters: BlacklistFilters = {}) =>
  useQuery({
    queryKey: [...BLACKLIST_KEY, filters],
    queryFn: async () => {
      const {data} = await getBlacklistService(filters);
      if (data === undefined) throw new Error("No data");
      return data;
    },
  });

export const useBlacklistEntry = (id: string) =>
  useQuery({
    queryKey: [...BLACKLIST_KEY, id],
    queryFn: async () => {
      const {data} = await getBlacklistEntryByIdService(id);
      if (data === undefined) throw new Error("No data");
      return data;
    },
    enabled: !!id,
  });

export const useCreateBlacklistEntry = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateBlacklistBody) => createBlacklistEntryService(body),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: BLACKLIST_KEY});
    },
  });
};

export const useActivateBlacklistEntry = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => activateBlacklistEntryService(id),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: BLACKLIST_KEY});
    },
  });
};

export const useDeactivateBlacklistEntry = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deactivateBlacklistEntryService(id),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: BLACKLIST_KEY});
    },
  });
};
